import moment from 'moment';

function generateMonthlyList() {
  const monthlyData = [];
  const currentMonth = moment().month();
  const currentYear = moment().year(); // Get the current year

  // Calculate the starting month and year for the last 14 months
  const startMonth = (currentMonth - 14 + 12) % 12;
  const startYear = currentMonth < 14 ? currentYear - 1 : currentYear;

  for (let i = 0; i < 15; i += 1) {
    const monthIndex = (startMonth + i) % 12; // Calculate the month index
    const monthYear = moment()
      .month(monthIndex)
      .year(startYear + Math.floor((startMonth + i) / 12))
      .format('MMMM (YYYY)'); // Set the year dynamically
    monthlyData.push(monthYear); // Add to the end of the array
  }
  return monthlyData;
}

function generatePeriodList(monthList, periods) {
  const periodList = [];
  const monthsPerPeriod = 3; // 3 months per period (quarter)

  // Make sure we start with January
  if (monthList[0]?.toLowerCase() !== 'jan') {
    console.warn('Warning: monthList does not start with January');
  }

  for (let i = 0; i < periods; i += 1) {
    const startIdx = i * monthsPerPeriod;
    const endIdx = startIdx + monthsPerPeriod - 1;

    if (startIdx < monthList.length && endIdx < monthList.length) {
      const startMonth = monthList[startIdx];
      const endMonth = monthList[endIdx];

      const periodLabel = `${startMonth.toUpperCase()} - ${endMonth.toUpperCase()}`;
      const periodValue = `${startMonth.toLowerCase()}-${endMonth.toLowerCase()}`;
      periodList.push({ value: periodValue, label: periodLabel });
    } else {
      break; // Stop if we don't have enough months
    }
  }

  return periodList;
}

function generateFullMonthList(monthList) {
  const fullMonthList = monthList.map((month) => ({
    value: month.toLowerCase(),
    label: month.toUpperCase(),
  }));
  return fullMonthList;
}
function getYearsList() {
  const currentDate = moment();
  const sixMonthsAgo = moment(currentDate).subtract(6, 'months');
  const startYear = sixMonthsAgo.year();
  const endYear = currentDate.year();
  const years = [];
  years.push({ label: startYear.toString(), value: startYear });
  if (startYear !== endYear) {
    years.push({ label: endYear.toString(), value: endYear });
  }

  return years;
}

function getLastFiveYears() {
  const currentDate = moment();
  const currentYear = currentDate.year();
  const years = [];

  for (let i = 0; i < 5; i += 1) {
    const year = currentYear - i;
    years.push({ value: year, label: year.toString() });
  }
  return years;
}

function generateQuarterlyList(monthList) {
  const quarterlyList = [];
  const currentDate = moment();

  // Get current quarter and year
  const currentMonth = currentDate.month(); // 0-11
  const currentYear = currentDate.year();
  const currentQuarter = Math.floor(currentMonth / 3) + 1; // 1-4

  // Month names for display
  const monthNames = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
  ];

  // Generate 6 quarters going backwards from current
  for (let i = 0; i < 6; i += 1) {
    // Calculate which quarter we're looking at (going backwards)
    let quarterOffset = currentQuarter - i;
    let yearOffset = 0;

    // Adjust year when quarter goes negative
    while (quarterOffset <= 0) {
      quarterOffset += 4;
      yearOffset -= 1;
    }

    const quarter = quarterOffset;
    const year = currentYear + yearOffset;

    // Calculate start and end months for this quarter
    const startMonthIndex = (quarter - 1) * 3; // 0, 3, 6, 9
    const endMonthIndex = startMonthIndex + 2;

    const startMonth = monthNames[startMonthIndex];
    const endMonth = monthNames[endMonthIndex];

    // Calculate the actual dates
    const startDate = moment(
      `${year}-${String(startMonthIndex + 1).padStart(2, '0')}-01`,
      'YYYY-MM-DD'
    );

    // For end date, get the last day of the end month
    const endDate = moment(
      `${year}-${String(endMonthIndex + 1).padStart(2, '0')}-01`,
      'YYYY-MM-DD'
    ).endOf('month');

    // Format dates as YYYY-MM-DD
    const startDateFormatted = startDate.format('YYYY-MM-DD');
    const endDateFormatted = endDate.format('YYYY-MM-DD');

    // Check if we have these months in our monthList (optional validation)
    const startMonthFormatted = `${startMonth.charAt(0) + startMonth.slice(1).toLowerCase()} (${year})`;
    const endMonthFormatted = `${endMonth.charAt(0) + endMonth.slice(1).toLowerCase()} (${year})`;

    const hasStartMonth = monthList.some((m) =>
      m.includes(startMonthFormatted)
    );
    const hasEndMonth = monthList.some((m) => m.includes(endMonthFormatted));

    quarterlyList.push({
      value: `${startDateFormatted} - ${endDateFormatted}`,
      label: `${startMonth} - ${endMonth} (${year}-${year})`,
      shortLabel: `Q${quarter} ${year}`,
      available: hasStartMonth && hasEndMonth, // Optional flag to indicate if data exists
    });
  }

  console.log(quarterlyList, 'quarterlyList');
  return quarterlyList;
}

function generateWeeklyList(monthList, dayOfWeek = 'Friday') {
  const weeklyList = [];
  const currentDate = moment();

  // Map day names to moment.js day numbers (0 = Sunday, 1 = Monday, etc.)
  const dayMap = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  const targetDayVal = dayMap[dayOfWeek.toLowerCase()] || 5; // Default to Friday (5)
  const targetDay = targetDayVal - 1;

  // Find the week that contains today's date
  // First, find the end date of the current week (the target day)
  let endDate = moment().day(targetDay);

  // Only go back if the target day is MORE than 6 days away (i.e., next week's occurrence)
  // This ensures the current week's Friday is always included even if it's in the future
  if (endDate.diff(currentDate, 'days') > 6) {
    endDate = endDate.subtract(1, 'week');
  }

  // Generate 18 weeks going backwards from current week
  for (let i = 0; i < 90; i += 1) {
    // Calculate week start and end dates
    const weekEndDate = moment(endDate).subtract(i, 'weeks');
    const weekStartDate = moment(weekEndDate).subtract(6, 'days');

    // Format dates for display
    const startFormatted = weekStartDate.format('YYYY-MM-DD');
    const endFormatted = weekEndDate.format('YYYY-MM-DD');

    // Create readable labels
    const startDisplay = weekStartDate.format('MMM D, YYYY');
    const endDisplay = weekEndDate.format('MMM D, YYYY');

    // Get week number and year for short label
    const weekNumber = weekEndDate.week();
    const year = weekEndDate.year();

    // Check if the current week contains today's date
    const containsToday = currentDate.isBetween(
      weekStartDate,
      weekEndDate,
      'day',
      '[]'
    );

    // Check if the months in this week exist in monthList
    const monthsInWeek = new Set();
    const currentCheck = moment(weekStartDate);
    while (currentCheck <= weekEndDate) {
      monthsInWeek.add(currentCheck.format('MMMM (YYYY)'));
      currentCheck.add(1, 'day');
    }

    // Handle different monthList formats
    let hasMonths = false;

    if (monthList && monthList.length > 0) {
      if (typeof monthList[0] === 'string') {
        hasMonths = Array.from(monthsInWeek).every((month) =>
          monthList.some((m) => m.includes(month) || m === month)
        );
      } else if (
        monthList[0] &&
        typeof monthList[0] === 'object' &&
        monthList[0].label
      ) {
        const monthStrings = monthList.map((m) => m.label || m);
        hasMonths = Array.from(monthsInWeek).every((month) =>
          monthStrings.some((m) => m.includes(month))
        );
      } else {
        hasMonths = true;
      }
    } else {
      hasMonths = true;
    }

    weeklyList.push({
      value: `${startFormatted} - ${endFormatted}`,
      label: `${startDisplay} - ${endDisplay}`,
      shortLabel: `Week ${weekNumber} (${year})`,
      startDate: startFormatted,
      endDate: endFormatted,
      weekNumber,
      year,
      dayOfWeek,
      isCurrentWeek: i === 0, // First item is the current week (the one with the most recent target day)
      containsToday, // Flag to identify if this week contains today
      available: hasMonths,
    });
  }

  return weeklyList;
}

const monthList = generateMonthlyList();
const yearsList = getYearsList();
const fiveYearsList = getLastFiveYears();
const monthlyList = generateFullMonthList(monthList);
const quarterlyList = generateQuarterlyList(monthList);
const halfYearlyList = generatePeriodList(monthList, 2);

// Then add it to your exports
const weeklyList = generateWeeklyList(monthList, 'Friday'); // Default to Friday
export {
  quarterlyList,
  halfYearlyList,
  monthlyList,
  yearsList,
  monthList,
  fiveYearsList,
  weeklyList,
  generateWeeklyList, // Export the function so it can be called with different days
};
